import { DESIGN_MODE_CUSTOM_CSS_SCRIPT_SELECTOR } from "@/constants";
import { REVIEWS_WIDGET_NAMES } from "@/constants/widget-names";
import { growaveConfigurationToken } from "@/tokens";
import { isShadowRoot } from "@/utils/isShadowRoot/isShadowRoot";
import { reviewsIconCollectionManagerContextToken } from "@apps/reviews/context_tokens";
import ReviewsStorefrontValuesService from "@apps/reviews/services/ReviewsStorefrontValuesService";
import {
    reviewsAuthApiClientToken,
    reviewsIconCollectionManagerToken,
} from "@apps/reviews/tokens";
import CustomizationManager from "@lib/CustomizationManager/CustomizationManager";
import { ProductIdManagerInterface } from "@lib/ProductIdManager/ProductIdManagerInterface";
import { CommonModule } from "@modules/common_module/CommonModule";
import { DesignModeProviderInterface } from "@modules/common_module/DesignModeProvider/DesignModeProviderInterface";
import { I18nModule } from "@modules/i18n/I18nModule";
import { container, injectable } from "tsyringe";

import { LoggerInterface } from "@interfaces/LoggerInterface";
import { WidgetInterface } from "@interfaces/WidgetInterface";

import AverageReviewsWidgetComponent from "./AverageReviewsWidget.svelte";
import { AverageReviewsWidgetConfigInterface } from "./AverageReviewsWidgetConfig";
import ReviewsListInfoQuery from "./queries/ReviewsListInfoQuery";
import ReviewsProductInfoQuery from "./queries/ReviewsProductInfoQuery";
import ReviewsListInfoService from "./services/ReviewsListInfoService";
import ReviewsProductInfoService from "./services/ReviewsProductInfoService";
import ReviewsListInfoStore from "./stores/ReviewsListInfoStore";
import ReviewsProductInfoStore from "./stores/ReviewsProductInfoStore";

@injectable()
class AverageReviewsWidget implements WidgetInterface {
    private readonly reviewsStorefrontValuesService: ReviewsStorefrontValuesService;
    private readonly reviewsProductInfoStore: ReviewsProductInfoStore;
    private readonly reviewsProductInfoService: ReviewsProductInfoService;
    private readonly reviewsProductInfoQuery: ReviewsProductInfoQuery;
    private readonly designModeProvider: DesignModeProviderInterface;

    private readonly reviewsListInfoStore: ReviewsListInfoStore;
    private readonly reviewsListInfoService: ReviewsListInfoService;
    private readonly reviewsListInfoQuery: ReviewsListInfoQuery;
    private readonly i18nModule: I18nModule;

    constructor(
        private readonly config: AverageReviewsWidgetConfigInterface,
        private readonly logger: LoggerInterface,
        private readonly productIdManager: ProductIdManagerInterface
    ) {
        this.reviewsStorefrontValuesService = container.resolve(
            ReviewsStorefrontValuesService
        );

        // I18n
        this.i18nModule = container.resolve(I18nModule);
        this.i18nModule.exports.i18n.registerLoader(
            "reviews.AverageReviewsWidget",
            this.i18nModule.exports.i18nApiLoaderFactory.getLoader(
                "reviews.AverageReviewsWidget"
            )
        );
        this.i18nModule.exports.i18n.registerLoader(
            "reviews.critical.AverageReviewsWidget",
            this.i18nModule.exports.i18nCriticalLoaderFactory.getLoader(
                "reviews.critical.AverageReviewsWidget"
            )
        );

        this.logger.debug("AverageReviewsWidget is initiaized", this.config);
        const authApi = container.resolve(reviewsAuthApiClientToken);
        this.reviewsProductInfoStore = new ReviewsProductInfoStore();
        this.reviewsProductInfoService = new ReviewsProductInfoService(
            this.reviewsProductInfoStore,
            authApi,
            logger
        );
        this.reviewsProductInfoQuery = new ReviewsProductInfoQuery(
            this.reviewsProductInfoStore
        );

        this.reviewsListInfoStore = new ReviewsListInfoStore();
        this.reviewsListInfoService = new ReviewsListInfoService(
            this.reviewsListInfoStore,
            authApi,
            this.logger
        );
        this.reviewsListInfoQuery = new ReviewsListInfoQuery(
            this.reviewsListInfoStore
        );

        this.designModeProvider =
            container.resolve(CommonModule).exports.designModeProvider;
    }

    async init(): Promise<this> {
        const customizationManager = container.resolve(CustomizationManager);
        const growaveConfiguration = container.resolve(
            growaveConfigurationToken
        );

        const element = this.config.target;
        const shadowRoot = isShadowRoot(
            this.config.isWidgetShadowRootEnabled,
            growaveConfiguration.isShadowRootDisabledGlobally
        )
            ? element.shadowRoot || element.attachShadow({ mode: "open" })
            : null;

        const customizationManagerConfig = {
            widgetName: REVIEWS_WIDGET_NAMES.AVERAGE_REVIEWS_WIDGET,
            placeholder: element,
            shadowRoot,
        };

        customizationManager.injectStylesFromBlockCustomCss({
            element,
            shadowRoot,
            scriptSelector: DESIGN_MODE_CUSTOM_CSS_SCRIPT_SELECTOR,
        });
        customizationManager.injectStylesFromCustomization(
            customizationManagerConfig
        );
        await customizationManager.beforeWidgetInject(
            customizationManagerConfig
        );

        const contextMap = new Map();
        contextMap.set(
            reviewsIconCollectionManagerContextToken,
            container.resolve(reviewsIconCollectionManagerToken)
        );

        new AverageReviewsWidgetComponent({
            target: shadowRoot || element,
            props: {
                reviewsStorefrontValuesService:
                    this.reviewsStorefrontValuesService,
                productIdManager: this.productIdManager,
                reviewsProductInfoService: this.reviewsProductInfoService,
                reviewsProductInfoQuery: this.reviewsProductInfoQuery,

                // ReviewsListInfo
                reviewsListInfoQuery: this.reviewsListInfoQuery,
                reviewsListInfoService: this.reviewsListInfoService,
                designModeProvider: this.designModeProvider,

                gwI18n: this.i18nModule.exports.i18n,
                afterWidgetInject: () =>
                    customizationManager.afterWidgetInject(
                        customizationManagerConfig
                    ),
                elementToScrollId: this.config.elementToScrollId,
                elementToClickId: this.config.elementToClickId,
            },
            context: contextMap,
        });

        return this;
    }
}

export default AverageReviewsWidget;
